import axios from "axios";
import store from "../Component/store/Provider";
import {
  CLOSE_LOADER,
  LOADER_OPEN,
} from "../Component/store/dialogue/dialogue.type";
import { apiKey, baseURL } from "../util/config";
import { getAuthToken } from "../util/authToken";

const client = axios.create({
  baseURL,
  timeout: 15000,
});

export const createCancel = () => {
  const controller = new AbortController();
  const cancel = (reason) => controller.abort(reason);

  return { controller, signal: controller.signal, cancel };
};

client.interceptors.request.use(
  (config) => {
    store.dispatch({ type: LOADER_OPEN });
    const token = getAuthToken();
    const headers = { ...config.headers };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (apiKey) {
      headers.key = apiKey;
    }

    return {
      ...config,
      headers,
    };
  },
  (error) => {
    store.dispatch({ type: CLOSE_LOADER });
    return Promise.reject(error);
  }
);

client.interceptors.response.use(
  (response) => {
    store.dispatch({ type: CLOSE_LOADER });
    const payload = response.data;
    const meta =
      payload?.meta ||
      payload?.pagination ||
      payload?.pageInfo ||
      (payload?.total !== undefined ? { total: payload.total } : undefined) ||
      (payload?.totalUsers !== undefined ? { totalUsers: payload.totalUsers } : undefined) ||
      (payload?.totalProducts !== undefined
        ? { totalProducts: payload.totalProducts }
        : undefined) ||
      (payload?.totalOrders !== undefined ? { totalOrders: payload.totalOrders } : undefined) ||
      (response.headers?.["x-total-count"] !== undefined
        ? { total: Number(response.headers["x-total-count"]) }
        : undefined);
    return { ok: true, data: payload, meta, status: response.status };
  },
  (error) => {
    store.dispatch({ type: CLOSE_LOADER });
    const isTimeout =
      error.code === "ECONNABORTED" ||
      (typeof error.message === "string" &&
        error.message.toLowerCase().includes("timeout"));
    if (isTimeout) {
      const message = error.message || "Request timed out";
      const details = {
        message,
        code: "TIMEOUT",
      };
      return Promise.resolve({
        ok: false,
        status: 0,
        message,
        code: "TIMEOUT",
        details,
        data: details,
      });
    }
    const status = error.response?.status ?? 0;
    const responseData = error.response?.data;
    const details =
      responseData !== undefined ? responseData : { message: error.message || "Request failed" };
    return Promise.resolve({
      ok: false,
      status,
      message: details?.message || error.message || "Request failed",
      code: details?.code || error.code,
      details,
      data: details,
    });
  }
);

export const apiClient = {
  get: (url, config) => client.get(url, config),
  post: (url, data, config) => client.post(url, data, config),
  put: (url, data, config) => client.put(url, data, config),
  patch: (url, data, config) => client.patch(url, data, config),
  delete: (url, config) => client.delete(url, config),
};

export default client;
